import { ApiRequestError } from "@nexxt/api-client";
import { IssueList as Issues, issueTexts } from "@nexxt/app-kit";
import type { ValidationIssue } from "@nexxt/contracts";
import type { CatalogIssueRule } from "@nexxt/contracts/catalog";
import { ERP_ITEM_COLUMNS } from "@nexxt/contracts/erp/item-file";
import { useCatalogUi } from "../catalog-ui";
import { m } from "../paraglide/messages.js";

// Why the domain refused an action, or what it still needs (422 `details.issues`, checkLinesheet): every problem,
// by field and row, in the app's words (principi-design.md, point 9).

/**
 * Names of the fields of a linesheet, of its lines and prices, of the Price Setup and of the SKU attributes: in forms,
 * grids, issues and history.
 */
const fieldLabels: Record<string, () => string> = {
  brandOrgId: m.col_brand,
  season: m.col_season,
  name: m.col_name,
  category: m.col_category,
  salesCampaignId: m.field_sales_campaign,
  inboundDeliveryFrom: m.field_inbound_delivery_from,
  inboundDeliveryTo: m.field_inbound_delivery_to,
  outboundDeliveryFrom: m.field_outbound_delivery_from,
  outboundDeliveryTo: m.field_outbound_delivery_to,
  lines: m.col_lines,
  syncToBc: m.col_bc_sync,
  syncToJoor: m.field_sync_to_joor,
  priceStatus: m.col_prices,
  bcSyncStatus: m.col_bc_sync,
  joorSyncStatus: m.col_joor_sync,
  inboundDelivery: m.field_inbound_delivery,
  outboundDelivery: m.field_outbound_delivery,
  product: m.field_line,
  sku: m.field_sku,
  skuCode: m.field_sku_code,
  brand: m.col_brand,
  size: m.col_size,
  colour: m.col_colour,
  priceList: m.field_price_list,
  supplier: m.field_supplier,
  lineCount: m.col_lines,
  status: m.col_status,
  styleCode: m.field_style_code,
  itemCode: m.field_tomorrow_style_code,
  tomorrowColourCode: m.field_tomorrow_colour_code,
  colourDescription: m.col_colour,
  sizes: m.col_sizes,
  brandPrices: m.field_brand_prices,
  position: m.field_position,
  productId: m.field_product,
  styleName: m.col_style_name,
  styleDescription: m.field_style_description,
  gender: m.field_gender,
  productCategory: m.field_product_category,
  productSubcategory: m.field_product_subcategory,
  sizeRunCategory: m.field_size_run_category,
  sizeRun: m.field_size_run,
  primaryColour: m.field_primary_colour,
  secondaryColour: m.field_secondary_colour,
  colourCombination: m.field_colour_combination,
  materialComposition: m.field_material_composition,
  countryOfOrigin: m.field_country_of_origin,
  costCurrency: m.field_cost_currency,
  unitRmCost: m.field_unit_rm_cost,
  freightCost: m.field_freight_cost,
  importDutyCost: m.field_import_duty_cost,
  miscCost: m.field_misc_cost,
  warehouseCost: m.field_warehouse_cost,
  notes: m.field_notes,
  isCarryover: m.badge_carryover,
  brandWholesalePrice: m.price_brand_wholesale,
  brandRetailPrice: m.price_brand_retail,
  brandPurchasePrice: m.price_brand_purchase,
  purchaseCurrency: m.price_purchase_currency,
  purchasePrice: m.price_purchase,
  wholesalePrice: m.price_wholesale,
  retailPrice: m.price_retail,
  prices: m.col_prices,
  calculated: m.field_prices_calculated,
  keptOverrides: m.field_overrides_kept,
  removed: m.field_prices_removed,
  attributesStatus: m.field_attributes_status,
  isActive: m.setup_is_active,
  finance: m.setup_tab_finance,
  operations: m.setup_tab_operations,
  sales: m.setup_tab_sales,
  fxRates: m.setup_fx_rates,
  currencies: m.setup_currencies,
  distributionDiscountPct: m.setup_distribution_discount,
  tomorrowMarginPct: m.setup_tomorrow_margin,
  retailMultiplier: m.setup_retail_multiplier,
  extraMarginPct: m.setup_extra_margin,
  freightInPct: m.setup_freight_in,
  dutiesInPct: m.setup_duties_in,
  warehousingPct: m.setup_warehousing,
  freightOutPct: m.setup_freight_out,
  dutiesOutPct: m.setup_duties_out,
  skus: m.field_unlocked_skus,
  ean: m.col_ean,
  customsClassification: m.attribute_customs_classification,
  hsCode: m.attribute_hs_code,
  commodityCode: m.attribute_commodity_code,
  countryWhenceConsigned: m.attribute_country_whence_consigned,
  manufacturerId: m.attribute_manufacturer,
  supplierStyleCode: m.attribute_supplier_style_code,
  supplierCode: m.attribute_supplier_code,
  purchaseDescription: m.attribute_purchase_description,
  tomorrowColourDescription: m.attribute_tomorrow_colour,
  matrixColourDescription: m.attribute_matrix_colour,
  composition: m.field_material_composition,
  compositionExtras: m.attribute_composition_extras,
  washingCareInstructions: m.attribute_washing_care,
  animalScientificName: m.attribute_animal_scientific_name,
  animalMaterialPct: m.attribute_animal_material_pct,
  animalCountryOfOrigin: m.attribute_animal_country,
  farmRaised: m.attribute_farm_raised,
  dyed: m.attribute_dyed,
  weightGross: m.attribute_weight_gross,
  weightNet: m.attribute_weight_net,
  weightUnit: m.attribute_weight_unit,
  rowsOk: m.import_rows_ok,
  rowsFailed: m.import_rows_failed,
};

const rules: Record<CatalogIssueRule, () => string> = {
  required: m.issue_required,
  after_start: m.issue_after_start,
  not_empty: m.issue_not_empty,
  delete_requested: m.issue_delete_requested,
  unknown_code: m.issue_unknown_code,
  size_not_in_run: m.issue_size_not_in_run,
  carryover_locked: m.issue_carryover_locked,
  not_carryover: m.issue_not_carryover,
  already_sent: m.issue_already_sent,
  duplicate_product: m.issue_duplicate_product,
  not_approved: m.issue_not_approved,
  not_enabled: m.issue_not_enabled,
  not_confirmed: m.issue_not_confirmed,
  missing_price: m.issue_missing_price,
  locked: m.issue_locked,
  ean_taken: m.issue_ean_taken,
  invalid_combination: m.issue_invalid_combination,
  not_in_setup: m.issue_not_in_setup,
  no_default_currency: m.issue_no_default_currency,
  stale: m.issue_stale,
  missing_mapping: m.issue_missing_mapping,
  ambiguous_mapping: m.issue_ambiguous_mapping,
  sku_code_taken: m.issue_sku_code_taken,
  codes_locked: m.issue_codes_locked,
  codes_taken: m.issue_codes_taken,
  invalid_value: m.issue_invalid_value,
  not_synced: m.issue_not_synced,
  invalid_format: m.issue_invalid_format,
  file_unreadable: m.issue_file_unreadable,
  file_too_much_text: m.issue_file_too_much_text,
  missing_column: m.issue_missing_column,
  duplicate_column: m.issue_duplicate_column,
  read_only_column: m.issue_read_only_column,
  no_rows: m.issue_no_rows,
  too_many_rows: m.issue_too_many_rows,
  not_editable: m.issue_not_editable,
  import_aborted: m.issue_import_aborted,
  not_found: m.issue_not_found,
  duplicate_row: m.issue_duplicate_row,
  ambiguous: m.issue_ambiguous,
  unknown_file: m.issue_unknown_file,
  wrong_folder: m.issue_wrong_folder,
  duplicate_file: m.issue_duplicate_file,
  too_many: m.issue_too_many,
  too_long: m.issue_too_long,
  truncated: m.issue_truncated,
};

/**
 * The name of a field in the app's words; the raw name when the catalog does not know it. A brand price in the
 * history names its price list and currency after a colon (`brandWholesalePrice:EXW-IT/EUR`).
 */
export function fieldLabel(field: string): string {
  const [name = field, detail] = field.split(":");
  const label = fieldLabels[name]?.() ?? erpColumnLabel(name) ?? name;
  return detail ? `${label} · ${detail}` : label;
}

/** A column of the file of the items to the ERP (`erp.<field>`, ADR-257), by its name in the file. */
function erpColumnLabel(name: string): string | undefined {
  if (!name.startsWith("erp.")) return undefined;
  const field = name.slice("erp.".length);
  const column = ERP_ITEM_COLUMNS.find((c) => c.field === field)?.name ?? field;
  return m.erp_file_column({ column });
}

/** A rule in the app's words, and the issue of one field for the form that shows it next to the control. */
export const { ruleText, fieldIssue } = issueTexts(rules);

/**
 * A problem in words: field and rule from the catalog, with the value the rule is about when the issue names it. A
 * field the catalog does not name (the missing code of another system) has the app's words.
 */
function issueText(
  { field, rule, row, value }: ValidationIssue,
  fieldText: ((field: string) => string | undefined) | undefined,
): string {
  const label = fieldText?.(field) ?? fieldLabel(field);
  const text = `${value === undefined ? label : `${label} “${value}”`}: ${ruleText(rule)}`;
  return row === undefined ? text : m.issue_on_row({ row: String(row), text });
}

/** The issues of the catalog under a title: what a check found, or what a refusal named. */
export function IssueList({ title, issues }: { title: string; issues: ValidationIssue[] }) {
  const { fieldText } = useCatalogUi();
  return <Issues title={title} issues={issues} text={(issue) => issueText(issue, fieldText)} />;
}

/** The issues of a refused action (422), or nothing. */
export function RefusedIssues({ error, title }: { error: unknown; title: string }) {
  return <IssueList title={title} issues={error instanceof ApiRequestError ? error.issues : []} />;
}
